import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PlusCircle, Pencil, Trash2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';

const ROLE_COLORS = { admin: 'bg-purple-100 text-purple-800', staff: 'bg-gray-100 text-gray-700' };

export default function UserList() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);

    useEffect(() => {
        api.get('/users')
            .then(r => setUsers(r.data))
            .catch(() => toast.error('Failed to load users'))
            .finally(() => setLoading(false));
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this user?')) return;
        setDeleting(id);
        try {
            await api.delete(`/users/${id}`);
            setUsers(prev => prev.filter(u => u.id !== id));
            toast.success('User deleted');
        } catch {
            toast.error('Failed to delete user');
        } finally {
            setDeleting(null);
        }
    };

    return (
        <div>
            <Toaster position="top-right" richColors />
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Users</h2>
                        <p className="text-sm text-gray-500 mt-1">Manage staff and admin accounts</p>
                    </div>
                    <button onClick={() => navigate('/admin/users/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition">
                        <PlusCircle className="w-4 h-4" /> Add User
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>
                ) : (
                    <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>{['Name', 'Email', 'Role', 'Created', 'Actions'].map(h => <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {users.length === 0 ? (
                                    <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400">No users found</td></tr>
                                ) : users.map(u => (
                                    <tr key={u.id} className="hover:bg-gray-50">
                                        <td className="px-5 py-3 font-medium text-gray-900">{u.name}</td>
                                        <td className="px-5 py-3 text-sm text-gray-600">{u.email}</td>
                                        <td className="px-5 py-3">
                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-700'}`}>
                                                {u.role}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3 text-sm text-gray-500">{new Date(u.created_at).toLocaleDateString()}</td>
                                        <td className="px-5 py-3">
                                            <div className="flex gap-2">
                                                <button onClick={() => navigate(`/admin/users/${u.id}/edit`)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-md"><Pencil className="w-4 h-4" /></button>
                                                <button onClick={() => handleDelete(u.id)} disabled={deleting === u.id} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50">
                                                    {deleting === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
